import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import { csatListShown, csatWithOperators } from "../../utils/analyticsCapabilities";
import { NO_COMPARISON, variation } from "../../utils/analyticsDeltas";
import { toBars } from "../../utils/analyticsLabels";
import { roundedShare } from "../../utils/analyticsSentences";
import { csatConversationsLink } from "../../utils/conversationFilters";
import ChartFrame from "../charts/ChartFrame";
import DataTable from "../charts/DataTable";
import InfoTip from "../charts/InfoTip";
import { formatCount, formatFixed, formatPercent } from "../charts/format";
import BarsFigure from "./BarsFigure";
import KpiStrip from "./KpiStrip";
import { countText } from "./kpis";
import Segmented from "./Segmented";

const card = { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" };

// O CSAT do separador Conversas, em "Como acabam" (D12 do plano do CSAT): quatro numeros, a distribuicao
// pelas estrelas e, num chat com operadores, os dois grupos (com operador e so com o assistente).
// Um valor sem denominador chega a null e le-se "n/d": o Intl.NumberFormat leria null como 0.
const GROUPS = ["all", "with_operator", "assistant_only"];
// O grupo escolhido no `handed_off` da ligacao para o visualizador: "Com operador" e a fase humana,
// "So com o assistente" e o contrario, e "Todas" nao filtra.
const GROUP_HANDED_OFF = { all: null, with_operator: true, assistant_only: false };
const STARS = [1, 2, 3, 4, 5];
const linkClass = "underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-mint)]";

// `commentsLink` e null para quem nao pode abrir conversas.
const CsatNumbers = ({ csat, previousComplete, commentsLink }) => {
  const { translate, language } = useLanguage();
  const { all, headline } = csat;
  const notAvailable = translate("analyticsNotAvailable");
  // Sem comentarios a lista abria vazia: nao ha ligacao.
  const showCommentsLink = commentsLink && csat.with_comment > 0;
  const share = all.ratings ? translate("analyticsCsatWithCommentContext", { share: `${roundedShare(csat.with_comment, all.ratings)}%` }) : null;
  const change = (name) => (previousComplete ? variation({ kind: "percent", ...headline[name], better: "up" }, language) : NO_COMPARISON);
  const kpis = [
    {
      key: "response_rate",
      label: translate("analyticsCsatResponseRate"),
      value: all.response_rate_pct === null ? notAvailable : formatPercent(all.response_rate_pct, language),
      change: change("response_rate_pct"),
      context: translate("analyticsCsatResponseRateContext", {
        ratings: countText(all.ratings, "analyticsCsatRatingCount", translate, language),
        requested: countText(all.requested, "analyticsCsatRequestCount", translate, language),
      }),
    },
    {
      key: "satisfaction",
      label: translate("analyticsCsatSatisfaction"),
      value: all.satisfaction_pct === null ? notAvailable : formatPercent(all.satisfaction_pct, language),
      change: change("satisfaction_pct"),
      context: translate("analyticsCsatSatisfactionContext"),
    },
    {
      key: "average",
      label: translate("analyticsCsatAverage"),
      value: all.average === null ? notAvailable : translate("analyticsCsatAverageValue", { value: formatFixed(all.average, 1, language) }),
      context: translate("analyticsCsatAverageContext", { ratings: countText(all.ratings, "analyticsCsatRatingCount", translate, language) }),
    },
    {
      key: "with_comment",
      label: translate("analyticsCsatWithComment"),
      info: translate("analyticsCsatWithCommentNote"),
      value: formatCount(csat.with_comment, language),
      context:
        share || showCommentsLink ? (
          <>
            {share && <span>{share}</span>}
            {share && showCommentsLink && " · "}
            {showCommentsLink && (
              <Link to={commentsLink} className={linkClass}>
                {translate("analyticsViewConversations")}
              </Link>
            )}
          </>
        ) : null,
    },
  ];
  return <KpiStrip label={translate("analyticsCsatNumbers")} kpis={kpis} />;
};

// A tabela dos dois grupos: valores crus nas linhas (o ficheiro descarregado leva-os assim) e o "n/d" no
// `format` de cada coluna.
const GroupsTable = ({ csat }) => {
  const { translate, language } = useLanguage();
  const notAvailable = translate("analyticsNotAvailable");
  const orDash = (format) => (value) => (value === null ? notAvailable : format(value));
  const table = {
    columns: [
      { key: "label", label: translate("analyticsGroup") },
      { key: "requested", label: translate("analyticsCsatRequests"), numeric: true, format: (value) => formatCount(value, language) },
      { key: "ratings", label: translate("analyticsCsatRatings"), numeric: true, format: (value) => formatCount(value, language) },
      { key: "response_rate_pct", label: translate("analyticsCsatResponseRate"), numeric: true, format: orDash((value) => formatPercent(value, language)) },
      { key: "satisfaction_pct", label: translate("analyticsCsatSatisfaction"), numeric: true, format: orDash((value) => formatPercent(value, language)) },
      { key: "average", label: translate("analyticsCsatAverage"), numeric: true, format: orDash((value) => formatFixed(value, 1, language)) },
    ],
    rows: ["with_operator", "assistant_only"].map((group) => ({ key: group, label: translate(`analyticsCsatGroup.${group}`), ...csat[group] })),
  };
  return (
    // `tableOnly`: o conteudo ja e a tabela, a moldura so lhe da o descarregar (sem o "Ver tabela").
    <ChartFrame title={translate("analyticsCsatGroups")} note={translate("analyticsCsatAssistantOnlyNote")} table={table} tableOnly>
      <DataTable caption={translate("analyticsCsatGroups")} columns={table.columns} rows={table.rows} />
    </ChartFrame>
  );
};

// A lista por operador, num cartao como o das perguntas escritas do Conteudo (o ChartFrame nao tem
// esqueleto, vazio nem erro). Sem `byOperator` nem erro o pedido ainda vem a caminho.
// `linkOf(operator)` e a ligacao para as conversas avaliadas desse operador.
const OperatorsList = ({ byOperator, error, onRetry, linkOf }) => {
  const { translate, language } = useLanguage();
  const headingId = useId();
  const title = translate("analyticsCsatByOperator");
  const notAvailable = translate("analyticsNotAvailable");
  const orDash = (format) => (value) => (value === null ? notAvailable : format(value));
  let body;
  if (error) {
    body = (
      <div role="alert" className="text-sm space-y-2" style={{ color: "var(--color-error-text)" }}>
        <p>{apiErrorMessage(error, translate("analyticsLoadFailed"))}</p>
        <button type="button" onClick={onRetry} className="px-3 py-1.5 text-sm rounded-lg border" style={{ ...card, color: "var(--color-text-primary)" }}>
          {translate("analyticsRetry")}
        </button>
      </div>
    );
  } else if (!byOperator) {
    body = (
      <div aria-busy="true" className="relative">
        <span className="sr-only">{translate("loading")}</span>
        <div className="h-24 rounded-xl animate-pulse" style={{ backgroundColor: "var(--color-surface-raised)" }} />
      </div>
    );
  } else {
    body = (
      <>
        {byOperator.limited_by_retention && (
          <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
            {byOperator.retention_days === 1
              ? translate("analyticsLimitedByRetentionOneDay")
              : translate("analyticsLimitedByRetention", { days: byOperator.retention_days })}
          </p>
        )}
        {byOperator.operators.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
            {translate("analyticsCsatByOperatorEmpty")}
          </p>
        ) : (
          <DataTable
            caption={title}
            columns={[
              { key: "name", label: translate("analyticsCsatOperator") },
              { key: "ratings", label: translate("analyticsCsatRatings"), numeric: true, format: (value) => formatCount(value, language) },
              { key: "satisfaction_pct", label: translate("analyticsCsatSatisfaction"), numeric: true, format: orDash((value) => formatPercent(value, language)) },
              { key: "average", label: translate("analyticsCsatAverage"), numeric: true, format: orDash((value) => formatFixed(value, 1, language)) },
              {
                key: "link",
                label: <span className="sr-only">{translate("analyticsViewConversations")}</span>,
                numeric: true,
                format: ({ to, name }) => (
                  <Link to={to} aria-label={translate("analyticsCsatViewOperatorConversations", { name })} className={`${linkClass} whitespace-nowrap text-xs`} style={{ color: "var(--color-text-secondary)" }}>
                    {translate("analyticsViewConversations")}
                  </Link>
                ),
              },
            ]}
            rows={byOperator.operators.map((operator) => {
              // Sem nome, a mesma legenda da pastilha do visualizador, e o link sai sem `operator_name`.
              const name = operator.name || translate("conversationsChipOperatorUnnamed");
              return { key: operator.id, ...operator, name, link: { to: linkOf(operator), name } };
            })}
          />
        )}
      </>
    );
  }
  return (
    <section aria-labelledby={headingId} className="rounded-xl border p-4 space-y-3" style={card}>
      <div className="flex items-center gap-1">
        <h4 id={headingId} className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {title}
        </h4>
        <InfoTip text={translate("analyticsCsatByOperatorNote")} label={translate("analyticsAbout", { title })} />
      </div>
      {body}
    </section>
  );
};

const CsatBlock = ({ summary, period, previousComplete, canOpenConversations, byOperator, byOperatorError, onRetryByOperator }) => {
  const { translate } = useLanguage();
  const headingId = useId();
  const [chosenGroup, setGroup] = useState("all");
  const { csat } = summary;
  const withOperators = csatWithOperators(summary);
  // O grupo guardado so vale com operadores: sem eles volta a todas, sem rebentar.
  const group = withOperators ? chosenGroup : "all";
  const title = translate("analyticsCsatTitle");
  const distributionTitle = translate("analyticsCsatDistribution");
  // As ligacoes para o visualizador, so para quem pode abrir conversas, como as do Conteudo: o chat e o
  // periodo da pagina, pelo fecho (D16).
  const linkTo = (filters) => csatConversationsLink({ chatId: summary.chat.id, period, ...filters });
  const info = withOperators ? translate("analyticsCsatNote") : `${translate("analyticsCsatNote")} ${translate("analyticsCsatAssistantOnlyNote")}`;
  return (
    <section aria-labelledby={headingId} className="space-y-3 min-w-0">
      <div className="flex items-center gap-1">
        <h3 id={headingId} className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {title}
        </h3>
        <InfoTip text={info} label={translate("analyticsAbout", { title })} />
      </div>
      {csat.all.requested === 0 ? (
        <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
          {translate("analyticsCsatNoRequests")}
        </p>
      ) : (
        <>
          {!csat.enabled && (
            <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
              {translate("analyticsCsatTurnedOff")}
            </p>
          )}
          <CsatNumbers csat={csat} previousComplete={previousComplete} commentsLink={canOpenConversations ? linkTo({ commented: true }) : null} />
          <div className="space-y-3">
            {withOperators && (
              <Segmented
                label={translate("analyticsCsatRatingsFrom")}
                options={GROUPS.map((value) => ({ value, label: translate(`analyticsCsatGroup.${value}`) }))}
                value={group}
                onChange={setGroup}
              />
            )}
            <BarsFigure
              title={distributionTitle}
              bars={toBars({ total: csat[group].ratings, values: csat[group].distribution, unattributed: 0 }, "rating", translate)}
              downloadName={`${distributionTitle} ${translate(`analyticsCsatGroup.${group}`)}`}
            />
            {/* Uma fila de ligacoes por baixo do grafico, e nao as barras, que nao sao botoes. */}
            {canOpenConversations && csat[group].ratings > 0 && (
              <nav aria-label={translate("analyticsCsatSeeByStars")} className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                {translate("analyticsCsatSeeByStars")}:{" "}
                {/* Uma estrela sem avaliacoes no grupo nao leva a lista nenhuma: sem ligacao. */}
                {STARS.filter((rating) => csat[group].distribution[rating] > 0).map((rating, index) => (
                  <React.Fragment key={rating}>
                    {index > 0 && " · "}
                    <Link to={linkTo({ rating, handedOff: GROUP_HANDED_OFF[group] })} className={linkClass}>
                      {translate(`analytics.rating.${rating}`)}
                    </Link>
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>
          {withOperators && <GroupsTable csat={csat} />}
          {csatListShown(summary, canOpenConversations) && (
            <OperatorsList
              byOperator={byOperator}
              error={byOperatorError}
              onRetry={onRetryByOperator}
              linkOf={(operator) => linkTo({ operator: { id: operator.id, name: operator.name } })}
            />
          )}
        </>
      )}
    </section>
  );
};

export default CsatBlock;
